import React from 'react';
import { Button, Grid, Icon } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import { Column, Options, Action } from 'material-table';
import { AxiosError, AxiosResponse } from 'axios';

import { VehicleContext } from '../VehicleContext';
import { MediaModel, mediaModelInitial } from '../../../Models/MediaModel';
import { getTableColumn, validateSpecialCharacterVehicle } from '../../../shared/operators';
import { CirculationModel, circulationModelInitial } from '../../../Models/CirculationModel';
import MediaService from '../../../Services/MediaService';
import CirculationService from '../../../Services/CirculationService';
import VehicleService from '../../../Services/VehicleService';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { TableEmpty } from '../../../shared/components/TableEmpty';
import { TableLoading } from '../../../shared/components/TableLoading';
import { TableCommon } from '../../../shared/components/TableCommon';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { HomeContext } from '../../Home/HomeContext';
import { VehicleModel, vehicleModelInitial } from '../../../Models/VehicleModel';
import { SearchStatus } from '../../../shared/enum/SearchStatus';
import { BasicPage } from '../../../shared/components/BasicPage';

const useStyles = makeStyles((theme: Theme) => createStyles({
    searchButtonGrid: {
        display: 'flex',
        width: '100%',
        textAlign: 'right',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(1.5, 0)
    },
    button: {
        paddingLeft: 0,
        '&:hover': {
            textDecorationLine: 'underline',
        },
    }
}));

export function VehicleSearch() {
    const classes = useStyles();

    const vehicleContext = React.useContext(VehicleContext);
    const homeContext = React.useContext(HomeContext);

    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
    const [vehicleDelete, setVehicleDelete] = React.useState<VehicleModel>(vehicleModelInitial);
    const [vehicleName, setVehicleName] = React.useState<string>('');
    const [selectedMediaType, setSelectedMediaType] = React.useState<MediaModel>(mediaModelInitial);
    const [selectedCirculation, setSelectedCirculation] = React.useState<CirculationModel>(circulationModelInitial);
    const [hasSpecialCharacter, setHasSpecialCharacter] = React.useState(false);
    const [searchStatus, setSearchStatus] = React.useState<SearchStatus>(SearchStatus.INITIAL);
    const [mediaTypeList, setMediaTypeList] = React.useState<MediaModel[]>([]);
    const [circulationList, setCirculationList] = React.useState<CirculationModel[]>([]);

    React.useEffect(() => {
        MediaService.mediaService()
            .getAll()
            .then((res: AxiosResponse) => {
                if (res.data) {
                    setMediaTypeList(res.data);
                }
            })
            .catch((error: AxiosError) => {
                homeContext.showError('Ops.. ocorreu um erro e não foi possível carregar os tipos de mídia.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            });

        CirculationService.circulationService()
            .getAll()
            .then((res: AxiosResponse) => {
                if (res.data)
                    setCirculationList(res.data);
            })
            .catch((error: AxiosError) => {
                homeContext.showError('Ops.. ocorreu um erro e não foi possível carregar as circulações.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            });
    }, []);

    const tableColumns: Column<VehicleModel>[] = [
        getTableColumn('Nome', 'nomenclature', 'string', 'left', {width: '30%'}),
        getTableColumn('Mídia', 'media.name', 'string', 'left', {width: '15%'}),
        getTableColumn('Circulação', 'circulation.name', 'string', 'left', {width: '20%'}),
        getTableColumn('Categoria editorial', 'editorialCategory.name', 'string', 'left', {width: '25%'})
    ];

    const tableActions: (Action<any> | ((rowData: any) => Action<any>))[] = [
        {
            icon: 'edit',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Editar veículo',
            onClick: (_, rowData: VehicleModel) => {
                vehicleContext.setVehicle(rowData);
                homeContext.setPageLocation(PageLocation.VEHICLE_EDIT);
            },
        },
        {
            icon: 'delete_outline',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Deletar veículo',
            onClick: (_, rowData: VehicleModel) => {
                setShowConfirmDelete(true);
                setVehicleDelete(rowData);
            },
        }
    ];

    const tableOptions: Options<VehicleModel> = {
        filtering: true,
        toolbar: false,
        showTitle: false,
        actionsColumnIndex: -1,
        pageSizeOptions: [10, 50, 100, 200],
        pageSize: 50,
    };

    const validateName = () => { setHasSpecialCharacter(validateSpecialCharacterVehicle(vehicleName ?? '')); };

    async function handleSearch() { // REVISAO: async function ou const?
        if (oneFieldFilledIn() && !hasSpecialCharacter) {
            setSearchStatus(SearchStatus.LOADING);

            VehicleService.getVehicleByQuery(vehicleName, selectedMediaType.id, selectedCirculation.id)
                .then((res: AxiosResponse) => {
                    if (res.data) {
                        vehicleContext.setVehicleList(res.data);
                        if (res.data.length > 0)
                            setSearchStatus(SearchStatus.RESULT);
                        else
                            setSearchStatus(SearchStatus.EMPTY);
                    }
                })
                .catch((error) => {
                    vehicleContext.setVehicleList([]);
                    setSearchStatus(SearchStatus.EMPTY);
                    homeContext.showError('Ops.. ocorreu um erro e não foi possível realizar a busca.' +
                        'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                });
        }
    }

    const deleteVehicle = () => {
        setShowConfirmDelete(false);

        VehicleService.vehicleService()
            .remove(vehicleDelete.id)
            .then(() => {
                const vehicles = [...vehicleContext.vehicleList];
                const index = vehicles.findIndex(item => item.id == vehicleDelete.id);
                vehicles.splice(index, 1);
                vehicleContext.setVehicleList(vehicles);
                homeContext.showSuccess('O veículo foi excluído com sucesso!');
            })
            .catch((error: AxiosError) => {
                homeContext.showError('Ops.. ocorreu um erro e não foi possível excluir o veículo. ' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            });
    };

    const oneFieldFilledIn = () => {
        const nameFieldFilled = vehicleName.length > 0;
        const mediaFieldFilled = selectedMediaType.id > 0;
        const circulationFieldFilled = selectedCirculation.id > 0;

        return nameFieldFilled || mediaFieldFilled || circulationFieldFilled;
    };

    const handleCleanFilter = () => {
        setVehicleName('');
        setSelectedCirculation(circulationModelInitial);
        setSelectedMediaType(mediaModelInitial);
    };

    const getVehicleName = () => {
        return (
            <Grid item xs={12} sm={4}>
                <TextField
                    label="Nome do veículo"
                    variant="outlined"
                    fullWidth
                    value={vehicleName}
                    onChange={(event) => setVehicleName(event.target.value) }
                    onBlur={validateName}
                    error={hasSpecialCharacter}
                    helperText={hasSpecialCharacter ? 'Caracteres especiais não são permitidos, altere a sua pesquisa.' : ''}
                    inputProps={{ maxLength: 100 }} />
            </Grid>
        );
    };

    const getMediaType = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id="media-type"
                    value={selectedMediaType}
                    onChange={(_, value: MediaModel | null) => {
                        if (value)
                            setSelectedMediaType(value);
                        else
                            setSelectedMediaType(mediaModelInitial);
                    }}
                    options={mediaTypeList}
                    getOptionLabel={(option: MediaModel) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) => 
                        <TextField
                            {...params}
                            label="Tipo de mídia"
                            variant="outlined" />
                    }
                />
            </Grid>
        );
    };

    const getCirculation = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id="circulation"
                    value={selectedCirculation}
                    onChange={(_, value: CirculationModel | null) => {
                        if (value)
                            setSelectedCirculation(value);
                        else
                            setSelectedCirculation(circulationModelInitial);
                    }}
                    options={circulationList}
                    getOptionLabel={(option: CirculationModel) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Circulação"
                            variant="outlined" />
                    }
                />
            </Grid>
        );
    };

    const getSearchButtons = () => {
        return (
            <Grid
                container
                spacing={1}
                className={classes.searchButtonGrid}>
                <Grid item>
                    <Button
                        onClick={handleCleanFilter}
                        size='large'
                        disabled={searchStatus == SearchStatus.LOADING || !oneFieldFilledIn()}>
                        LIMPAR FILTROS
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        onClick={handleSearch}
                        variant='contained'
                        color='primary'
                        size='large'
                        disabled={searchStatus == SearchStatus.LOADING || !oneFieldFilledIn()}>
                        BUSCAR
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const getAddNewButton = () => {
        return (
            <Grid item xs={12}>
                <Button
                    onClick={() => homeContext.setPageLocation(PageLocation.VEHICLE_INPUT)}
                    size='large'
                    startIcon={<Icon color='primary'>add</Icon>}
                    className={classes.button}>
                    ADICIONAR NOVO VEÍCULO
                </Button>
            </Grid>
        );
    };

    const getSearchTable = () => {
        return (
            <Grid item xs={12}>
                {searchStatus == SearchStatus.INITIAL &&
                    <TableEmpty displayText='Os resultados de busca aparecerão aqui' />}
                {searchStatus == SearchStatus.LOADING &&
                        <TableLoading displayText='Carregando veículos...' />}
                {searchStatus == SearchStatus.RESULT &&
                <>
                    {getAddNewButton()}
                    <TableCommon
                        tableData={vehicleContext.vehicleList}
                        tableColumns={tableColumns}
                        emptyDataSourceMessage='Nenhum veículo encontrado.'
                        tableActions={tableActions}
                        extraOptions={tableOptions} />
                </>}
                {searchStatus == SearchStatus.EMPTY &&
                    <TableEmpty
                        displayText='Nenhum veículo foi encontrado'
                        buttonText='ADICIONAR NOVO VEÍCULO'
                        pageLocation={PageLocation.VEHICLE_INPUT} />}
            </Grid>
        );
    };

    return (
        <BasicPage
            title="Consulta de veículos"
            subtitle="Para consultar os veículos cadastrados na base, insira um filtro e clique em buscar."
            dialogs={ <BasicConfirmDialog
                title={'Tem certeza que deseja excluir este veículo?'}
                description={'Ao excluir este veículo, ele ficará inativo para novas classificações.'}
                handleFirstButton={() => deleteVehicle()}
                handleSecondButton={() => setShowConfirmDelete(false)}
                textFirstButton={'Excluir'}
                textSecondButton={'Cancelar'}
                open={showConfirmDelete}/> }>
            <>
                {getVehicleName()}
                {getMediaType()}
                {getCirculation()}
                {getSearchButtons()}
                {getSearchTable()}
            </>
        </BasicPage>
    );
}
